// src/newDashboard/layout/DashboardShell.jsx
//
// The frame around the owner and charity dashboards.
//
//   Computer: a menu down the left side, pages on the right.
//   Phone:    a slim bar at the top (logo + "Your bar"), the main menu as
//             a bar along the bottom of the screen (like the customer app),
//             and each section's pages as big buttons at the top.
//
// sections = [{ label, short, to, icon, tabs: [{ label, to } | { label, href }] }]

import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import "./OwnerShell.css";

function cleanPath(path) {
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

function sectionFor(sections, pathname) {
  const path = cleanPath(pathname);
  return (
    sections.find((s) => s.tabs.some((t) => t.to && cleanPath(t.to) === path)) ||
    sections.find((s) => cleanPath(s.to) === path) ||
    sections[0]
  );
}

export default function DashboardShell({ sections, roleLabel, barPicker, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const current = sectionFor(sections, location.pathname);
  const path = cleanPath(location.pathname);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  return (
    <div className="os-shell">
      <aside className="os-side">
        <div className="os-top">
          <img className="os-logo" src="/logo-header.png" alt="Bar Fun Finder" />
          {roleLabel && <span className="os-role">{roleLabel}</span>}
          <button type="button" className="os-logout os-logout-top" onClick={handleLogout}>Log out</button>
        </div>

        {barPicker}

        <nav className="os-nav" aria-label="Dashboard">
          {sections.map((s) => (
            <NavLink
              key={s.label}
              to={s.to}
              end
              className={`os-nav-link ${current.label === s.label ? "active" : ""}`}
              aria-current={current.label === s.label ? "page" : undefined}
            >
              {s.icon}
              <span className="os-nav-long">{s.label}</span>
              <span className="os-nav-short">{s.short || s.label}</span>
            </NavLink>
          ))}
        </nav>

        <button type="button" className="os-logout os-logout-side" onClick={handleLogout}>Log out</button>
      </aside>

      <main className="os-main">
        {current.tabs.length > 0 && (
          <nav className="os-subtabs" aria-label={`${current.label} pages`}>
            {current.tabs.map((t) =>
              t.href ? (
                <a key={t.label} className="os-subtab" href={t.href} target="_blank" rel="noopener noreferrer">
                  {t.label} ↗
                </a>
              ) : (
                <NavLink
                  key={t.label}
                  to={t.to}
                  end
                  className={`os-subtab ${cleanPath(t.to) === path ? "active" : ""}`}
                >
                  {t.label}
                </NavLink>
              )
            )}
          </nav>
        )}
        {children}
      </main>
    </div>
  );
}
